import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Monitor, RefreshCw, Shield, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { openWorkspaceLiveView } from '@auw/api-client';
import { activeTenantAppName } from '@auw/tenant';
import type { WorkspaceMode } from '@auw/workspace-utils';

interface LiveViewPanelProps {
  liveViewRef: string;
  conversationId: string;
  workspaceType: WorkspaceMode | 'ppt';
  watchOnly: boolean;
  onClose: () => void;
  onSessionClosed?: (errorMessage?: string | null) => void;
}

function extractHttpStatus(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const matched = err.message.match(/HTTP\s+(\d+)/i);
  if (!matched) return null;
  const code = Number(matched[1]);
  return Number.isFinite(code) ? code : null;
}

export const LiveViewPanel: React.FC<LiveViewPanelProps> = ({
  liveViewRef,
  conversationId,
  workspaceType,
  watchOnly,
  onClose,
  onSessionClosed,
}) => {
  const { t } = useTranslation('common');
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setViewerUrl(null);

    void (async () => {
      try {
        const response = await openWorkspaceLiveView(workspaceType, {
          live_view_ref: liveViewRef,
          item_id: conversationId,
        });
        if (cancelled) return;
        setViewerUrl(response.viewer_url);
      } catch (err) {
        if (cancelled) return;
        const status = extractHttpStatus(err);
        setViewerUrl(null);
        setError(
          status === 404 || status === 410
            ? t('liveView.sessionEnded')
            : t('liveView.openError'),
        );
        if (status === 404 || status === 409 || status === 410) {
          onSessionClosed?.(status === 409 ? 'live_view_unavailable' : null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId, liveViewRef, onSessionClosed, retryNonce, workspaceType]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if ((data as { type?: unknown }).type !== 'auw-live-view-disconnected') return;
      const ref = typeof (data as { liveViewRef?: unknown }).liveViewRef === 'string'
        ? (data as { liveViewRef: string }).liveViewRef
        : null;
      if (ref && ref !== liveViewRef) return;
      onSessionClosed?.('browserbase-disconnected');
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [liveViewRef, onSessionClosed]);

  return (
    <Panel>
      <Header>
        <TitleRow>
          <Title>
            <Monitor size={18} />
            <span>{t('liveView.title')}</span>
          </Title>
          {watchOnly && (
            <ModeBadge>
              <Shield size={12} />
              <span>Watch-only</span>
            </ModeBadge>
          )}
        </TitleRow>
        <ControlGroup>
          {!!error && (
            <RetryButton type="button" onClick={() => setRetryNonce((prev) => prev + 1)}>
              <RefreshCw size={14} />
              <span>{t('button.retry')}</span>
            </RetryButton>
          )}
          <CloseButton type="button" onClick={onClose} aria-label={t('liveView.closeLiveView')}>
            <X size={18} />
          </CloseButton>
        </ControlGroup>
      </Header>
      <Content>
        {error ? (
          <StatusPanel>
            <StatusTitle>{t('liveView.loadFailed')}</StatusTitle>
            <StatusDescription>{error}</StatusDescription>
          </StatusPanel>
        ) : loading || !viewerUrl ? (
          <StatusPanel>
            <Spinner aria-hidden="true" />
            <StatusTitle>{t('liveView.preparing')}</StatusTitle>
            <StatusDescription>{t('liveView.pleaseWait')}</StatusDescription>
          </StatusPanel>
        ) : (
          <Frame
            src={viewerUrl}
            title={`${activeTenantAppName()} Live View`}
            allow="clipboard-read; clipboard-write"
            referrerPolicy="no-referrer"
          />
        )}
      </Content>
      <Footer>
        {watchOnly
          ? t('liveView.watchOnlyNotice')
          : t('liveView.connectionStatus')}
      </Footer>
    </Panel>
  );
};

const Panel = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 4px 16px rgba(0, 0, 0, 0.06),
    0 12px 40px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  min-height: 48px;
  flex-shrink: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ModeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.bgTertiary ?? theme.colors.bgHover};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.72rem;
  font-weight: 700;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgHover};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  background: #000;
`;

const Frame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: #000;
`;

const StatusPanel = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StatusTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
`;

const StatusDescription = styled.div`
  max-width: 420px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-top-color: ${({ theme }) => theme.colors.accent};
  animation: ${spin} 0.9s linear infinite;
`;

const Footer = styled.div`
  padding: 10px 14px 14px;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8rem;
  line-height: 1.5;
`;
